import React, { useRef, useState } from 'react';
import { Dialog, DialogContent } from '../../components/dialog';
import { Button } from '../../components/button';
import { X, Download, Eye } from 'lucide-react';
import { ZoomableImage } from './zoomable-image';

export interface ViewerItem {
    id: string;
    url: string;
    title?: string;
    alt?: string;
    type: 'image' | 'document' | 'video' | 'file';
    downloadUrl?: string;
}

interface PhotoDocumentViewerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    items: ViewerItem[];
    title?: string;
}

/**
 * Reusable modal component for viewing photos and documents
 * Displays all items in a single column grid layout
 */
export const PhotoDocumentViewerModal = ({
    open,
    onOpenChange,
    items,
}: PhotoDocumentViewerModalProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasZoomedImage, setHasZoomedImage] = useState(false);

    const handleDownload = (item: ViewerItem) => {
        if (item.downloadUrl || item.url) {
            const link = document.createElement('a');
            link.href = item.downloadUrl || item.url;
            link.download = item.title || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!open) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            if (!hasZoomedImage) {
                onOpenChange(false);
            }
        }
    };

    if (items.length === 0) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex-col overflow-hidden p-0"
                onKeyDown={handleKeyDown}
                closeButton={false}
                variant="fullScreen"
            >
                <Button
                    color="secondary"
                    size={'square36'}
                    onClick={() => onOpenChange(false)}
                    className="absolute top-4 right-6 z-100"
                >
                    <X className="h-6 w-6" />
                </Button>

                <div
                    className="relative min-h-0 flex-1 overflow-auto"
                    ref={containerRef}
                >
                    {/* Content area - Single column grid */}
                    <div className="flex flex-col gap-2 py-8">
                        {items.map((item, index) => (
                            <div
                                key={item.id}
                                className="flex h-full flex-col items-center"
                            >
                                {item.type === 'image' ? (
                                    <div className="relative h-full w-full">
                                        <ZoomableImage
                                            id={item.id}
                                            url={item.url}
                                            alt={item.alt}
                                            title={item.title}
                                            index={index}
                                            onKeyDown={handleKeyDown}
                                            onZoomChange={setHasZoomedImage}
                                        />
                                    </div>
                                ) : item.type === 'video' ? (
                                    <div className="relative w-full px-12">
                                        <div
                                            className="mx-auto w-fit overflow-hidden rounded-lg"
                                            style={{
                                                height: 'calc(100dvh - 10rem)',
                                            }}
                                        >
                                            <video
                                                src={item.url}
                                                controls
                                                className="h-full"
                                                preload="metadata"
                                            >
                                                Your browser does not support
                                                the video tag.
                                            </video>
                                        </div>
                                    </div>
                                ) : item.type === 'document' ? (
                                    <div className="relative w-full">
                                        <iframe
                                            src={`${item.url}#navpanes=0&scrollbar=0`}
                                            className="h-[90dvh] w-full"
                                            title={item.title || 'Document'}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex max-w-md flex-col items-center justify-center gap-4 text-center">
                                        <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-muted">
                                            <Eye className="h-12 w-12 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <h3 className="mb-2 text-lg font-semibold">
                                                {item.title || 'File'}
                                            </h3>
                                            <p className="mb-4 text-muted-foreground">
                                                This file cannot be previewed in
                                                the browser.
                                            </p>
                                            <Button
                                                onClick={() =>
                                                    handleDownload(item)
                                                }
                                                className="gap-2"
                                            >
                                                <Download className="h-4 w-4" />
                                                Download File
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
